// src/components/common/QRAccumulationFlow.jsx

import React, { useState } from 'react';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import QRScannerModal from '../modals/QRScannerModal';
import AccumulatePointsModal from '../modals/AccumulatePointsModal';
import QuickAccumulateModal from '../modals/QuickAccumulateModal';
import { qrErrorHandler } from '../../helpers/qrErrorHandler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode } from '@fortawesome/free-solid-svg-icons';

const QRAccumulationFlow = ({
    isOpen,
    onClose,
    productos = [],
    onAccumulateSuccess,
    tiendaId // ✅ NUEVO PARÁMETRO REQUERIDO
}) => {
    const [showQRScanner, setShowQRScanner] = useState(false);
    const [showAccumulateModal, setShowAccumulateModal] = useState(false);
    const [showQuickAccumulateModal, setShowQuickAccumulateModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleStartScanning = () => {
        setShowQRScanner(true);
    };

    const handleQRScanned = async (data) => {
        try {
            setLoading(true);

            const { ticket, qrData } = data;

            // Obtener información del cliente
            const clienteData = await get(`clientes/${ticket.cliente}`);

            if (!clienteData) {
                await qrErrorHandler.handleBackendError({
                    status: 404,
                    message: 'No se pudo obtener la información del cliente'
                });
                return;
            }

            // Verificar si se puede acumular puntos
            const canAccumulate = checkCanAccumulate(ticket, clienteData);

            if (!canAccumulate.canAccumulate) {
                await qrErrorHandler.showWarning('No se puede acumular', canAccumulate.reason);
                return;
            }

            // Configurar datos para el modal de acumulación
            setSelectedTicket(ticket);
            setSelectedCliente(clienteData);
            // Siempre mostrar modal completo (más opciones disponibles)
            // El usuario puede usar suma rápida desde ClientesList si lo necesita
            setShowAccumulateModal(true);

        } catch (error) {
            console.error('Error processing QR data:', error);
            await qrErrorHandler.handleBackendError(error);
        } finally {
            setLoading(false);
        }
    };

    const checkCanAccumulate = (ticket, cliente) => {
        // Verificar si el ticket está activo
        if (!ticket || !ticket.activo) {
            return { canAccumulate: false, reason: 'El ticket no está activo' };
        }

        // Verificar si el cliente está activo
        if (!cliente || !cliente.activo) {
            return { canAccumulate: false, reason: 'El cliente no está activo' };
        }

        // Verificar límites de puntos si es sistema de puntos
        if (ticket.tipoFidelizacion === 'PUNTOS') {
            const puntosActuales = ticket.puntos || 0;
            const puntosMaximos = ticket.marca?.puntosTicket || 0;

            if (puntosActuales >= puntosMaximos) {
                return {
                    canAccumulate: false,
                    reason: 'El cliente ya ha alcanzado el máximo de puntos'
                };
            }
        }

        return { canAccumulate: true };
    };

    const handleAccumulateSuccess = (result) => {
        // Cerrar todos los modales
        setShowAccumulateModal(false);
        setShowQuickAccumulateModal(false);
        setShowQRScanner(false);

        // Llamar al callback de éxito si existe
        if (onAccumulateSuccess) {
            onAccumulateSuccess(result);
        }

        // Cerrar el flujo completo
        onClose();
    };

    const handleCloseAccumulateModal = () => {
        setShowAccumulateModal(false);
        setSelectedTicket(null);
        setSelectedCliente(null);
    };

    const handleCloseQRScanner = () => {
        setShowQRScanner(false);
    };

    const handleCloseFlow = () => {
        setShowQRScanner(false);
        setShowAccumulateModal(false);
        setShowQuickAccumulateModal(false);
        setSelectedTicket(null);
        setSelectedCliente(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Modal principal de inicio */}
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-2 sm:p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-2 sm:mx-4">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                <FontAwesomeIcon icon={faQrcode} className="text-white text-xl" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">Acumular Puntos</h2>
                                <p className="text-xs text-white/80">Escanea el QR del cliente</p>
                            </div>
                        </div>
                        <button
                            onClick={handleCloseFlow}
                            className="text-white hover:text-gray-200 transition-colors p-1"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <div className="text-center mb-6">
                            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FontAwesomeIcon icon={faQrcode} className="text-purple-600 text-3xl" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                Escanear QR del Cliente
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Pide al cliente que muestre su ticket con código QR para acumular puntos automáticamente
                            </p>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={handleStartScanning}
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faQrcode} />
                                        Iniciar Escáner QR
                                    </>
                                )}
                            </button>

                            <div className="p-4 bg-blue-50 rounded-lg">
                                <h4 className="font-medium text-blue-900 mb-2 text-sm">Instrucciones:</h4>
                                <ul className="text-xs text-blue-800 space-y-1 text-left">
                                    <li>• Pide al cliente que abra su ticket digital</li>
                                    <li>• Haz clic en "Iniciar Escáner QR"</li>
                                    <li>• Apunta la cámara hacia el código QR</li>
                                    <li>• El sistema abrirá automáticamente el formulario de acumulación</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de escaneo QR */}
            {showQRScanner && (
                <QRScannerModal
                    isOpen={showQRScanner}
                    onClose={handleCloseQRScanner}
                    onQRScanned={handleQRScanned}
                />
            )}

            {/* Modal de acumulación de puntos completo */}
            {showAccumulateModal && selectedTicket && selectedCliente && (
                <AccumulatePointsModal
                    ticketId={selectedTicket._id}
                    montoConsumido={0}
                    setMontoConsumido={() => { }}
                    puntosMaximos={selectedTicket.marca?.puntosTicket || 0}
                    puntosAcumulados={selectedTicket.puntos || 0}
                    onClose={handleCloseAccumulateModal}
                    onAccumulate={handleAccumulateSuccess}
                    tipoFidelizacion={selectedTicket.tipoFidelizacion}
                    clienteId={selectedCliente._id}
                    productos={productos}
                    tiendaId={tiendaId} // ✅ PASAR TIENDA ID
                    marca={selectedTicket.marca} // ✅ PASAR MARCA PARA MOTOR DE PUNTOS
                />
            )}

            {/* Modal de suma rápida */}
            {showQuickAccumulateModal && selectedTicket && selectedCliente && (
                <QuickAccumulateModal
                    ticketId={selectedTicket._id}
                    puntosMaximos={selectedTicket.marca?.puntosTicket || 0}
                    puntosAcumulados={selectedTicket.puntos || 0}
                    onClose={() => {
                        setShowQuickAccumulateModal(false);
                        setSelectedTicket(null);
                        setSelectedCliente(null);
                    }}
                    onAccumulate={handleAccumulateSuccess}
                    onOpenFullModal={() => {
                        setShowQuickAccumulateModal(false);
                        setShowAccumulateModal(true);
                    }}
                    tipoFidelizacion={selectedTicket.tipoFidelizacion}
                    clienteId={selectedCliente._id}
                    tiendaId={tiendaId}
                    marca={selectedTicket.marca}
                />
            )}
        </>
    );
};

export default QRAccumulationFlow;
